import { CSSProperties, ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { Icon, IconName } from './Icon';
import { statusLabel, toneOf, Tone } from '../../lib/status';

/** Status pill: the code decides colour and wording; pass `label` to override the wording. */
export function Pill({ status, label, tone }: { status?: string | null; label?: string; tone?: Tone }) {
  const t = tone ?? toneOf(status);
  return <span className={clsx('pill', t)}>{label ?? statusLabel(status)}</span>;
}

export function Kpi({ label, value, delta, deltaTone, tone, extra, style, to }: { label: ReactNode; value: ReactNode; delta?: ReactNode; deltaTone?: 'up' | 'down'; tone?: 'mint' | 'amber' | 'rose'; extra?: ReactNode; style?: CSSProperties; to?: string }) {
  const t = tone ?? (deltaTone === 'down' ? 'rose' : deltaTone === 'up' ? 'mint' : '');
  const inner = (
    <>
      <span className="label">{label}</span>
      <span className="value">{value}</span>
      {delta ? <span className={clsx('delta', deltaTone)}>{delta}</span> : null}
      {extra}
      {to ? <span className="kpi-go" aria-hidden="true" /> : null}
    </>
  );
  // a card that goes somewhere is a real link, so it is keyboard-reachable and opens in a new tab on request
  return to
    ? <Link to={to} className={clsx('kpi', 'kpi-link', t)} style={style}>{inner}</Link>
    : <div className={clsx('kpi', t)} style={style}>{inner}</div>;
}

export function Panel({ children, className, style, id, onClick }: { children: ReactNode; className?: string; style?: CSSProperties; id?: string; onClick?: (e: React.MouseEvent<HTMLDivElement>) => void }) {
  return (
    <div id={id} className={clsx('panel', className)} style={style} onClick={onClick}>
      {children}
    </div>
  );
}

export function PanelHead({ title, children }: { title?: ReactNode; children?: ReactNode }) {
  return (
    <div className="panel-head">
      {/* a panel title is the next level under the page heading, so it is announced as a level 2 heading */}
      {title ? <h3 aria-level={2}>{title}</h3> : null}
      {children}
    </div>
  );
}

export const PanelBody = ({ children, className, style }: { children: ReactNode; className?: string; style?: CSSProperties }) => (
  <div className={clsx('panel-body', className)} style={style}>
    {children}
  </div>
);

export function PageHead({ title, sub, actions, crumbs }: { title: ReactNode; sub?: ReactNode; actions?: ReactNode; crumbs?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        {crumbs ? <div className="crumbs">{crumbs}</div> : null}
        <h1>{title}</h1>
        {sub ? <p>{sub}</p> : null}
      </div>
      {actions ? <div className="actions">{actions}</div> : null}
    </div>
  );
}

export function Callout({ tone = 'info', children, icon }: { tone?: 'good' | 'warn' | 'bad' | 'info'; children: ReactNode; icon?: IconName }) {
  const name: IconName = icon ?? (tone === 'good' ? 'check' : tone === 'info' ? 'info' : 'warn');
  return (
    <div className={clsx('callout', tone)} role={tone === 'bad' ? 'alert' : undefined}>
      <Icon name={name} />
      <div>{children}</div>
    </div>
  );
}

export function Meter({ pct }: { pct: number }) {
  const c = pct > 100 ? 'bad' : pct > 85 ? 'warn' : '';
  return (
    <div className={clsx('meter', c)} role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
      <i style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
    </div>
  );
}

export function Tabs<T extends string>({ tabs, value, onChange, slide: slideProp, slider }: { tabs: Array<[T, ReactNode]>; value: T; onChange: (v: T) => void; slide?: boolean; slider?: boolean }) {
  const slide = slideProp ?? slider; // `slider` is the older spelling of `slide`
  const ref = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState<{ left: number; width: number } | null>(null);
  // `slide`: one highlight that glides to the selected tab instead of each tab painting its own
  useLayoutEffect(() => {
    if (!slide) return;
    const el = ref.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (el) setPill({ left: el.offsetLeft, width: el.offsetWidth });
  }, [slide, value, tabs.length]);
  return (
    <div className={clsx('tabs', slide && 'slide')} role="tablist" ref={ref}>
      {slide && pill ? <span className="tab-pill" aria-hidden="true" style={{ width: pill.width, transform: `translateX(${pill.left}px)` }} /> : null}
      {tabs.map(([k, label]) => (
        <button key={k} role="tab" type="button" aria-selected={k === value} className={k === value ? 'on' : ''} onClick={() => onChange(k)}>
          {label}
        </button>
      ))}
    </div>
  );
}

export function Steps({ steps, done }: { steps: string[]; done: number }) {
  return (
    <div className="steps" aria-label="Progress">
      {steps.map((s, i) => (
        <div key={s} className={i < done ? 'done' : i === done ? 'now' : ''}>
          {s}
        </div>
      ))}
    </div>
  );
}

export function Empty({ title, children }: { title?: ReactNode; children?: ReactNode }) {
  return (
    <div className="empty">
      {/* a panel title is the next level under the page heading, so it is announced as a level 2 heading */}
      {title ? <h3 aria-level={2}>{title}</h3> : null}
      {children ? <p>{children}</p> : null}
    </div>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="empty" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" /> {label}
    </div>
  );
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg = error instanceof Error ? error.message : 'Something went wrong.';
  return (
    <div className="panel">
      <div className="empty">
        <h3>We could not load this</h3>
        <p>{msg}</p>
        {onRetry ? (
          <button className="btn" onClick={onRetry}>
            Try again
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function Btn({
  children, tone, size, icon, ...rest
}: { children?: ReactNode; tone?: 'primary' | 'good' | 'danger' | 'ghost'; size?: 'sm'; icon?: IconName } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type="button" {...rest} className={clsx('btn', tone, size, rest.className)}>
      {icon ? <Icon name={icon} /> : null}
      {children}
    </button>
  );
}

export function DefList({ items }: { items: Array<[ReactNode, ReactNode]> }) {
  return (
    <dl className="dl">
      {items.map(([k, v], i) => (
        <div key={i} style={{ display: 'contents' }}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Dialog rendered in a portal: Escape and a click outside close it; focus moves inside on open. */
export function Modal({ title, onClose, children, footer, wide }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // the latest onClose, so a parent that re-renders (typing in a field inside the dialog) does not re-run the focus set-up below
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>('input,textarea,select,button.primary,button');
    first?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close.current();
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, []);
  return createPortal(
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={clsx('modal', wide && 'wide')} role="dialog" aria-modal="true" aria-labelledby={titleId} ref={ref}>
        <div className="modal-head">
          <h2 id={titleId}>{title}</h2>
          <button className="btn ghost sm" onClick={onClose} aria-label="Close">
            <Icon name="x" />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

export function ListPlain({ children }: { children: ReactNode }) {
  return <ul className="list-plain">{children}</ul>;
}

export function IconBadge({ name, tone }: { name: IconName; tone?: 'good' | 'warn' | 'bad' | 'info' }) {
  return (
    <span className={clsx('ic', tone)}>
      <Icon name={name} />
    </span>
  );
}

export const DocNo = ({ children }: { children: ReactNode }) => <span className="doc-no">{children}</span>;
export const Sub = ({ children }: { children: ReactNode }) => <span className="sub">{children}</span>;
